import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import * as htmlToImage from 'html-to-image';
import YTDChartComponent from "./Components/ExFac/YTDChart"
import MonthlyChartComponent from './Components/ExFac/MonthlyChart';

const EndOfMonthSummary = forwardRef(function EndOfMonthSummary({ apiUrl, selectedDate }, ref) {

  function SectionCard({ title, subtitle, right, children, sx }) {
    return (
      <Card sx={{ borderRadius:2, border:'1px solid', borderColor:'divider', boxShadow:'0 4px 14px rgba(0,0,0,0.04)', ...sx }}>
        <CardContent sx={{ p: 2.5 }}>
          {(title || right || subtitle) && (
            <Box sx={{ mb: 1.5, display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
              <Box>
                {title && <Typography variant="h6" sx={{ fontWeight:700 }}>{title}</Typography>}
                {subtitle && <Typography variant="body2" sx={{ opacity:0.7, mt:0.25 }}>{subtitle}</Typography>}
              </Box>
              {right}
            </Box>
          )}
          {children}
        </CardContent>
      </Card>
    );
  }
  const [asOf, setAsOf] = useState('');
  const [title, setTitle] = useState('Cumulative Daily');

  const [fullMonthPointsData, setFullMonthPointsData] = useState([]);
  const [eoy_ForecastData, setEoyForecastData] = useState();
  const [c250_points_cash, set_C250_points_cash] = useState();
  const [c250_points_units, set_C250_points_units] = useState();
  const [eoy_C250_points_cash, setEOY_C250_points_cash] = useState([]);
  const [eoy_C250_points_units, setEOY_C250_points_units] = useState([]);
  const [c500_points_cash, set_C500_points_cash] = useState();
  const [c500_points_units, set_C500_points_units] = useState();
  const [eoy_C500_points_cash, setEOY_C500_points_cash] = useState([]);
  const [eoy_C500_points_units, setEOY_C500_points_units] = useState([]);

  const [simalvia_points_cash, set_Simalvia_points_cash] = useState();
  const [simalvia_points_units, set_Simalvia_points_units] = useState();
  const [eoy_simalvia_points_cash, setEOY_Simalvia_points_cash] = useState([]);
  const [eoy_simalvia_points_units, setEOY_Simalvia_points_units] = useState([]);

  const [transisoft_points_cash, set_transisoft_points_cash] = useState();
  const [transisoft_points_units, set_transisoft_points_units] = useState();
  const [eoy_transisoft_points_cash, setEOY_transisoft_points_cash] = useState([]);
  const [eoy_transisoft_points_units, setEOY_transisoft_points_units] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportMode, setExportMode] = useState(false);

  // Refs for export
  const chartRefTotalPortfolioMonthly = useRef(null);
  const chartRefTotalPortfolioYearly = useRef(null);

  const chartRefC250MonthlyCash = useRef(null);
  const chartRefC250MonthlyUnits = useRef(null);
  const chartRefC250YearlyCash = useRef(null);
  const chartRefC250YearlyUnits = useRef(null);

  const chartRefC500MonthlyCash = useRef(null);
  const chartRefC500MonthlyUnits = useRef(null);
  const chartRefC500YearlyCash = useRef(null);
  const chartRefC500YearlyUnits = useRef(null);

  const chartRefSimalviaMonthlyCash = useRef(null);
  const chartRefSimalviaMonthlyUnits = useRef(null);
  const chartRefSimalviaYearlyCash = useRef(null);
  const chartRefSimalviaYearlyUnits = useRef(null);
  
  const chartRefTransisoftMonthlyCash = useRef(null);
  const chartRefTransisoftMonthlyUnits = useRef(null);
  const chartRefTransisoftYearlyCash = useRef(null);
  const chartRefTransisoftYearlyUnits = useRef(null);

  // ---------------- Fetch ----------------
  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${apiUrl}/api/get_eom_summary`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedDate: selectedDate })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');

      setAsOf(json.as_of || '');
      setFullMonthPointsData(json.full_month_points);
      setEoyForecastData(json.eoy_forecast);

      set_C250_points_cash(json.c250_points_cash);
      set_C250_points_units(json.c250_points_units);
      setEOY_C250_points_cash(json.c250_yearly_points_cash);
      setEOY_C250_points_units(json.c250_yearly_points_units);
      
      set_C500_points_cash(json.c500_points_cash);
      set_C500_points_units(json.c500_points_units);
      setEOY_C500_points_cash(json.c500_yearly_points_cash);
      setEOY_C500_points_units(json.c500_yearly_points_units);

      set_Simalvia_points_cash(json.simalvia_points_cash);
      set_Simalvia_points_units(json.simalvia_points_units);
      setEOY_Simalvia_points_cash(json.simalvia_yearly_points_cash);
      setEOY_Simalvia_points_units(json.simalvia_yearly_points_units);

      set_transisoft_points_cash(json.transisoft_points_cash);
      set_transisoft_points_units(json.transisoft_points_units);
      setEOY_transisoft_points_cash(json.transisoft_yearly_points_cash);
      setEOY_transisoft_points_units(json.transisoft_yearly_points_units);
    } catch (e) {
      setError(String(e.message || e));
      setFullMonthPointsData([]);
      setEoyForecastData([]);
    } finally { setLoading(false); }
  }, [apiUrl, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---------------- Export helpers ----------------
  const filenameBase = React.useMemo(() => {
    const safeTitle = (title || 'chart').replace(/\s+/g, '_').replace(/[^\w\-_.]+/g, '');
    const safeDate  = (asOf || '').replace(/-/g, '');
    return `${safeTitle}_${safeDate || 'today'}`;
  }, [title, asOf]);

  async function loadImage(dataUrl) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });
  }
  async function nodeToPngDataUrl(node, { scale = 2, backgroundColor = '#ffffff' } = {}) {
    // fonts ready = crisp text
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch {}
    }
    return htmlToImage.toPng(node, {
      pixelRatio: scale,
      backgroundColor,
      cacheBust: true,
    });
  }
  // wait for N animation frames
  function rafFrames(n = 2) {
    return new Promise(resolve => {
      const step = () => (n-- <= 0 ? resolve() : requestAnimationFrame(step));
      requestAnimationFrame(step);
    });
  }

  // wait until element size is stable for a short quiet window
  async function waitForStableSize(node, quietMs = 100, timeoutMs = 1500) {
    if (!node) return;
    const start = performance.now();
    let lastW = 0, lastH = 0, lastChange = performance.now();

    while (performance.now() - start < timeoutMs) {
      await rafFrames(1);
      const { width, height } = node.getBoundingClientRect();
      if (width !== lastW || height !== lastH) {
        lastW = width; lastH = height; lastChange = performance.now();
      }
      if (performance.now() - lastChange >= quietMs) return;
    }
  }

  // wait until Recharts has drawn line/bar paths
  async function waitForRechartsDrawn(node, timeoutMs = 1500) {
    if (!node) return;
    const start = performance.now();
    while (performance.now() - start < timeoutMs) {
      await rafFrames(1);
      const svg = node.querySelector('svg');
      if (svg) {
        const hasLines = svg.querySelectorAll('.recharts-line path').length > 0;
        const hasBars  = svg.querySelectorAll('.recharts-bar-rectangle path, .recharts-bar-rectangle rect').length > 0;
        if (hasLines || hasBars) return;
      }
    }
  }

  async function handleExportPNG() {
    const EXPORT_WIDTH = 1000;
    const SCALE = 2;
    const PADDING = 24;
    const GAP = 24;
    const base = filenameBase || 'charts';

    // temp styles: hide tooltips & CSV button
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
      .no-export { display: none !important; }
      *:hover { pointer-events: none !important; }
    `;
    document.head.appendChild(styleEl);

    const prevExportMode = exportMode;
    setExportMode(true); // disables line animations in your YTDChart

    const nodes = [chartRefTotalPortfolioMonthly.current, chartRefTotalPortfolioYearly.current, chartRefC250MonthlyCash.current].filter(Boolean);
    const originals = nodes.map(n => ({ node: n, width: n.style.width }));
    nodes.forEach(n => { n.style.width = `${EXPORT_WIDTH}px`; });

    try {
      // Let layout settle & Recharts redraw
      await rafFrames(2);
      window.dispatchEvent(new Event('resize'));
      await waitForStableSize(document.body, 120);
      // Ensure each chart has drawn its paths
      for (const n of nodes) {
        await waitForStableSize(n, 80);
        await waitForRechartsDrawn(n);
      }

      // Capture each section
      const [png1, png2, png3] = await Promise.all([
        nodeToPngDataUrl(chartRefTotalPortfolioMonthly.current, { scale: SCALE }),
        nodeToPngDataUrl(chartRefTotalPortfolioYearly.current,  { scale: SCALE }),
        nodeToPngDataUrl(chartRefC250MonthlyCash.current,  { scale: SCALE }),
      ]);

      const [img1, img2, img3] = await Promise.all([
        loadImage(png1), loadImage(png2), loadImage(png3)
      ]);

      // Compose
      const pad = Math.round(PADDING * SCALE);
      const gap = Math.round(GAP * SCALE);
      const outW = EXPORT_WIDTH * SCALE + pad * 2;
      const outH = pad + img1.height + gap + img2.height + gap + img3.height + pad;

      const canvas = document.createElement('canvas');
      canvas.width = outW; canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, outW, outH);

      let top = pad, left = pad;
      ctx.drawImage(img1, left, top);
      top += img1.height + gap;
      ctx.drawImage(img2, left, top);
      top += img2.height + gap;
      ctx.drawImage(img3, left, top);

      const combinedUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = combinedUrl;
      a.download = `${base}__ALL.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      console.error(e);
      alert('Combined export failed.');
    } finally {
      originals.forEach(({ node, width }) => { node.style.width = width; });
      setExportMode(prevExportMode);
      document.head.removeChild(styleEl);
    }
  }
  
  useImperativeHandle(ref, () => ({
    async getExportPayload() {
      const EXPORT_WIDTH = 1000;
      const SCALE = 2;

      // 1) Hide tooltips + Export CSV during capture
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
        .no-export { display: none !important; }
        *:hover { pointer-events: none !important; }
      `;
      document.head.appendChild(styleEl);

      // 2) disable animations
      const prevExportMode = exportMode;
      setExportMode(true);

      // 3) Lock consistent width for each section
      const nodes = [chartRefTotalPortfolioMonthly.current, chartRefTotalPortfolioYearly.current, 
        chartRefC250MonthlyCash.current, chartRefC250MonthlyUnits.current, chartRefC250YearlyCash.current, chartRefC250YearlyUnits.current,
        chartRefC500MonthlyCash.current, chartRefC500MonthlyUnits.current, chartRefC500YearlyCash.current, chartRefC500YearlyUnits.current,
        chartRefSimalviaMonthlyCash.current, chartRefSimalviaMonthlyUnits.current, chartRefSimalviaYearlyCash.current, chartRefSimalviaYearlyUnits.current,
        chartRefTransisoftMonthlyCash.current, chartRefTransisoftMonthlyUnits.current, chartRefTransisoftYearlyCash.current, chartRefTransisoftYearlyUnits.current
      ].filter(Boolean);
      const originals = nodes.map(n => ({ node: n, width: n.style.width }));
      nodes.forEach(n => { n.style.width = `${EXPORT_WIDTH}px`; });

      try {
        // 4) Wait for layout + drawing to settle
        await rafFrames(2);
        window.dispatchEvent(new Event('resize'));
        await waitForStableSize(document.body, 120);
        for (const n of nodes) {
          await waitForStableSize(n, 80);
          await waitForRechartsDrawn(n);
        }

        // 5) Rasterize each section to PNG (same scale)
        const [png1, png2, png3, png4, png5, png6, png7, png8, png9, png10, png11, png12, png13, png14, png15, png16, png17, png18] = await Promise.all([
          nodeToPngDataUrl(chartRefTotalPortfolioMonthly.current, { scale: SCALE }),
          nodeToPngDataUrl(chartRefTotalPortfolioYearly.current,  { scale: SCALE }),

          nodeToPngDataUrl(chartRefC250MonthlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC250MonthlyUnits.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC250YearlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC250YearlyUnits.current,  { scale: SCALE }),

          nodeToPngDataUrl(chartRefC500MonthlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC500MonthlyUnits.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC500YearlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefC500YearlyUnits.current,  { scale: SCALE }),

          nodeToPngDataUrl(chartRefSimalviaMonthlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefSimalviaMonthlyUnits.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefSimalviaYearlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefSimalviaYearlyUnits.current,  { scale: SCALE }),

          nodeToPngDataUrl(chartRefTransisoftMonthlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefTransisoftMonthlyUnits.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefTransisoftYearlyCash.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefTransisoftYearlyUnits.current,  { scale: SCALE }),
        ]);

        await Promise.all([
          loadImage(png1), loadImage(png2), 
          loadImage(png3), loadImage(png4), loadImage(png5), loadImage(png6), 
          loadImage(png7), loadImage(png8), loadImage(png9), loadImage(png10),
          loadImage(png11), loadImage(png12), loadImage(png13), loadImage(png14),
          loadImage(png15), loadImage(png16), loadImage(png17), loadImage(png18)
        ]);

        const chartMainPng = png1;
        const chartYTDPng = png2;
        const c250MonthlyCashChart = png3;
        const c250MonthlyUnitsChart = png4;
        const c250YearlyCashChart = png5;
        const c250YearlyUnitsChart = png6;
        const c500MonthlyCashChart = png7;
        const c500MonthlyUnitsChart = png8;
        const c500YearlyCashChart = png9;
        const c500YearlyUnitsChart = png10;
        const simalviaMonthlyCashChart = png11;
        const simalviaMonthlyUnitsChart = png12;
        const simalviaYearlyCashChart = png13;
        const simalviaYearlyUnitsChart = png14;
        const transisoftMonthlyCashChart = png15;
        const transisoftMonthlyUnitsChart = png16;
        const transisoftYearlyCashChart = png17;
        const transisoftYearlyUnitsChart = png18;

        return {
          chartMainPng,
          chartYTDPng,
          c250MonthlyCashChart,
          c250MonthlyUnitsChart,
          c250YearlyCashChart,
          c250YearlyUnitsChart,
          c500MonthlyCashChart,
          c500MonthlyUnitsChart,
          c500YearlyCashChart,
          c500YearlyUnitsChart,
          simalviaMonthlyCashChart,
          simalviaMonthlyUnitsChart,
          simalviaYearlyCashChart,
          simalviaYearlyUnitsChart,
          transisoftMonthlyCashChart,
          transisoftMonthlyUnitsChart,
          transisoftYearlyCashChart,
          transisoftYearlyUnitsChart
        };
      } finally {
        // 7) Restore UI
        originals.forEach(({ node, width }) => { node.style.width = width; });
        setExportMode(prevExportMode);
        document.head.removeChild(styleEl);
      }
    }
  }));

  // ---------------- Render ----------------
  return (
    <Box sx={{ padding: 1, minHeight: '100vh' }}>
      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12}>
          <SectionCard
            title={'End of Month Summary'}
            right={
              <Box sx={{ display:'flex', gap: 1, flexWrap:'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportPNG}
                  sx={{ borderRadius: 999, textTransform:'none', fontWeight:600 }}
                >
                  Export PNG
                </Button>
              </Box>
            }
          >
            {loading && <CircularProgress size={22} />}
            {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && !error && (
              <>
                <MonthlyChartComponent chartRef={chartRefTotalPortfolioMonthly} title={"Total Portfolio — Cash"} data={fullMonthPointsData} exportMode={exportMode}/>
                <YTDChartComponent chartRef={chartRefTotalPortfolioYearly} title={"Total Portfolio - Cash"} ytdData={eoy_ForecastData} exportMode={exportMode} showPinnedBox={true}/>
                
                <MonthlyChartComponent chartRef={chartRefC250MonthlyCash} title={"C250 — Cash"} data={c250_points_cash} exportMode={exportMode}/>
                <MonthlyChartComponent chartRef={chartRefC250MonthlyUnits} title={"C250 — Units"} data={c250_points_units} exportMode={exportMode}/>
                <YTDChartComponent chartRef={chartRefC250YearlyCash} title={"C250 - Cash"} ytdData={eoy_C250_points_cash} exportMode={exportMode} showPinnedBox={true}/>
                <YTDChartComponent chartRef={chartRefC250YearlyUnits} title={"C250 - Units"} ytdData={eoy_C250_points_units} exportMode={exportMode} showPinnedBox={true}/>

                
                <MonthlyChartComponent chartRef={chartRefC500MonthlyCash} title={"C500 — Cash"} data={c500_points_cash} exportMode={exportMode}/>
                <MonthlyChartComponent chartRef={chartRefC500MonthlyUnits} title={"C500 — Units"} data={c500_points_units} exportMode={exportMode}/>
                <YTDChartComponent chartRef={chartRefC500YearlyCash} title={"C500 - Cash"} ytdData={eoy_C500_points_cash} exportMode={exportMode} showPinnedBox={true}/>
                <YTDChartComponent chartRef={chartRefC500YearlyUnits} title={"C500 - Units"} ytdData={eoy_C500_points_units} exportMode={exportMode} showPinnedBox={true}/>
                
                
                <MonthlyChartComponent chartRef={chartRefSimalviaMonthlyCash} title={"Simalvia — Cash"} data={simalvia_points_cash} exportMode={exportMode}/>
                <MonthlyChartComponent chartRef={chartRefSimalviaMonthlyUnits} title={"Simalvia — Units"} data={simalvia_points_units} exportMode={exportMode}/>
                <YTDChartComponent chartRef={chartRefSimalviaYearlyCash} title={"Simalvia - Cash"} ytdData={eoy_simalvia_points_cash} exportMode={exportMode} showPinnedBox={true}/>
                <YTDChartComponent chartRef={chartRefSimalviaYearlyUnits} title={"Simalvia - Units"} ytdData={eoy_simalvia_points_units} exportMode={exportMode} showPinnedBox={true}/>

                
                <MonthlyChartComponent chartRef={chartRefTransisoftMonthlyCash} title={"Transisoft — Cash"} data={transisoft_points_cash} exportMode={exportMode}/>
                <MonthlyChartComponent chartRef={chartRefTransisoftMonthlyUnits} title={"Transisoft — Units"} data={transisoft_points_units} exportMode={exportMode}/>
                <YTDChartComponent chartRef={chartRefTransisoftYearlyCash} title={"Transisoft - Cash"} ytdData={eoy_transisoft_points_cash} exportMode={exportMode} showPinnedBox={true}/>
                <YTDChartComponent chartRef={chartRefTransisoftYearlyUnits} title={"Transisoft - Units"} ytdData={eoy_transisoft_points_units} exportMode={exportMode} showPinnedBox={true}/>
              </>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
});

export default EndOfMonthSummary;

export async function captureImageForComponent(ref, {
  scale = 2,
  backgroundColor = '#ffffff'
} = {}) {
  const node = ref?.current;
  if (!node) return null;

  // Ensure fonts are loaded so text renders correctly
  // (safe to no-op if unsupported)
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch {}
  }

  // Optional: temporarily hide tooltips/crosshairs/hover effects
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-export-hide', 'true');
  styleEl.textContent = `
    .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
    .no-export { display: none !important; }
    *:hover { pointer-events: none !important; }
  `;
  document.head.appendChild(styleEl);

  try {
    // Render entire DOM node to PNG data URL
    const dataUrl = await htmlToImage.toPng(node, {
      pixelRatio: scale,
      backgroundColor,
      cacheBust: true,
    });

    // Convert to Image object so callers can measure w/h & draw on canvas
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    return { img, w, h, dataUrl };
  } finally {
    // clean up temp styles
    document.head.removeChild(styleEl);
  }
}